import AttachFileIcon from '@mui/icons-material/AttachFile'
import CancelIcon from '@mui/icons-material/Cancel'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DeleteIcon from '@mui/icons-material/Delete'
import LinkOffIcon from '@mui/icons-material/LinkOff'
import {
  Box,
  Button,
  Card,
  CardActions,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2 as Grid,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Paper,
  Typography,
} from '@mui/material'
import { useAtomValue } from 'jotai'
import React, { useRef, useState } from 'react'

import { currentUserAtom } from '../../domains/user/AtomUsers'
import ImageService from '../../services/ImageService'
import useBar from '../common/Bar'
import FullscreenMediaViewer from '../common/FullscreenMediaViewer'

/**
 * PictureManagement - Displays user's pictures in a Masonry layout with add/delete functionality
 * @param {Object} props
 * @param {Array} props.pictures - Array of picture objects with {resourceId, url} or just URL strings
 * @param {Function} props.onPicturesChange - Callback when pictures are added/deleted
 * @param {boolean} [props.editable=true] - Whether user can add/delete pictures
 */
const PictureManagement = ({ pictures = [], onPicturesChange, editable = true }) => {
  const [openUploadDialog, setOpenUploadDialog] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState([]) // Files selected but not uploaded yet
  const [uploadedImages, setUploadedImages] = useState([]) // Images uploaded but not confirmed
  const [isUploading, setIsUploading] = useState(false)
  const [isConfirming, setIsConfirming] = useState(false)
  const [deletingImageId, setDeletingImageId] = useState(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [pictureToDelete, setPictureToDelete] = useState(null)
  const [unlinkConfirmOpen, setUnlinkConfirmOpen] = useState(false)
  const [pictureToUnlink, setPictureToUnlink] = useState(null)
  const [isDragging, setIsDragging] = useState(false)
  const [openAttachDialog, setOpenAttachDialog] = useState(false)
  const [existingImages, setExistingImages] = useState([])
  const [selectedExistingImages, setSelectedExistingImages] = useState([])
  const [loadingExistingImages, setLoadingExistingImages] = useState(false)
  const [attachingImages, setAttachingImages] = useState(false)
  const [viewerOpen, setViewerOpen] = useState(false)
  const [viewerInitialIndex, setViewerInitialIndex] = useState(0)
  const fileInputRef = useRef(null)
  const bar = useBar()
  const user = useAtomValue(currentUserAtom)

  // Convert pictures to consistent format
  const normalizedPictures = pictures.map((pic, index) => {
    if (typeof pic === 'string') {
      return { url: pic, id: `pic-${index}` }
    }
    return { url: pic.url || pic.r2Url, id: pic.resourceId || `pic-${index}` }
  })

  const handleOpenUploadDialog = () => {
    setOpenUploadDialog(true)
    setSelectedFiles([])
    setUploadedImages([])
  }

  const handleCloseUploadDialog = () => {
    // Clean up uploaded images if dialog is closed without confirming
    if (uploadedImages.length > 0) {
      handleCancelAllUploads()
    }
    // Clean up preview URLs
    selectedFiles.forEach((file) => {
      if (file.previewUrl) {
        URL.revokeObjectURL(file.previewUrl)
      }
    })
    setOpenUploadDialog(false)
    setSelectedFiles([])
    setUploadedImages([])
  }

  /**
   * Validate a single file
   */
  const validateFile = (file) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
    if (!allowedTypes.includes(file.type)) {
      throw new Error(`Invalid file type: ${file.name}. Only JPEG, PNG, GIF, and WEBP are allowed.`)
    }

    const maxSize = 10 * 1024 * 1024 // 10MB
    if (file.size > maxSize) {
      throw new Error(`File size exceeds 10MB: ${file.name}`)
    }

    return true
  }

  /**
   * Handle file selection (from input or drop)
   */
  const handleFileSelect = (files) => {
    const fileArray = Array.from(files)
    const validFiles = []
    const errors = []

    fileArray.forEach((file) => {
      try {
        validateFile(file)
        const previewUrl = URL.createObjectURL(file)
        validFiles.push({
          file,
          previewUrl,
          id: `file-${Date.now()}-${Math.random()}`,
        })
      } catch (error) {
        errors.push(error.message)
      }
    })

    if (errors.length > 0) {
      bar.error(`Some files were rejected: ${errors.join(', ')}`)
    }

    if (validFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...validFiles])
      bar.info(`${validFiles.length} file(s) selected. Click "Upload" to upload them.`)
    }
  }

  /**
   * Remove a selected file from preview
   */
  const handleRemoveSelectedFile = (fileId) => {
    setSelectedFiles((prev) => {
      const fileToRemove = prev.find((f) => f.id === fileId)
      if (fileToRemove && fileToRemove.previewUrl) {
        URL.revokeObjectURL(fileToRemove.previewUrl)
      }
      return prev.filter((f) => f.id !== fileId)
    })
  }

  /**
   * Handle file input change
   */
  const handleFileInputChange = (event) => {
    if (event.target.files && event.target.files.length > 0) {
      handleFileSelect(event.target.files)
    }
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  /**
   * Handle drag events
   */
  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files)
    }
  }

  /**
   * Upload all selected files
   */
  const handleUploadFiles = async () => {
    if (selectedFiles.length === 0) {
      return
    }

    setIsUploading(true)

    try {
      const uploadPromises = selectedFiles.map(async (fileItem) => {
        const timestamp = Date.now()
        const randomId = Math.random().toString(36).substring(7)
        const imageData = await ImageService.uploadImage(fileItem.file, `gallery-${timestamp}-${randomId}`)
        return imageData
      })

      const uploadedData = await Promise.all(uploadPromises)
      setUploadedImages(uploadedData)

      // Clean up preview URLs
      selectedFiles.forEach((file) => {
        if (file.previewUrl) {
          URL.revokeObjectURL(file.previewUrl)
        }
      })
      setSelectedFiles([])

      bar.success(`${uploadedData.length} image(s) uploaded successfully. Click "Confirm" to add to your gallery.`)
    } catch (error) {
      console.error('Failed to upload images:', error)
      bar.error(error.message || 'Failed to upload images')
    } finally {
      setIsUploading(false)
    }
  }

  /**
   * Confirm adding uploaded images to gallery
   */
  const handleConfirmUpload = async () => {
    if (uploadedImages.length === 0) {
      return
    }

    setIsConfirming(true)

    try {
      const newPictures = uploadedImages.map((imageData) => ({
        resourceId: imageData.resourceId,
        url: imageData.r2Url,
      }))

      const updatedPictures = [...pictures, ...newPictures]

      if (onPicturesChange) {
        await onPicturesChange(updatedPictures)
      }

      bar.success(`${newPictures.length} picture(s) added to gallery successfully`)
      setUploadedImages([])
      setOpenUploadDialog(false)
    } catch (error) {
      console.error('Failed to add pictures:', error)
      bar.error(error.message || 'Failed to add pictures to gallery')
    } finally {
      setIsConfirming(false)
    }
  }

  /**
   * Cancel all uploads and delete uploaded images
   */
  const handleCancelAllUploads = async () => {
    if (uploadedImages.length === 0) {
      return
    }

    try {
      const deletePromises = uploadedImages.map((imageData) => ImageService.deleteImage(imageData.resourceId))
      await Promise.all(deletePromises)
      setUploadedImages([])
      bar.info('Uploaded images have been cancelled')
    } catch (error) {
      console.error('Failed to delete uploaded images:', error)
      setUploadedImages([])
    }
  }

  /**
   * Open delete confirmation dialog
   */
  const handleDeleteClick = (picture, index) => {
    setPictureToDelete({ picture, index })
    setDeleteConfirmOpen(true)
  }

  /**
   * Close delete confirmation dialog
   */
  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false)
    setPictureToDelete(null)
  }

  /**
   * Open unlink confirmation dialog
   */
  const handleUnlinkClick = (picture, index) => {
    setPictureToUnlink({ picture, index })
    setUnlinkConfirmOpen(true)
  }

  /**
   * Close unlink confirmation dialog
   */
  const handleUnlinkCancel = () => {
    setUnlinkConfirmOpen(false)
    setPictureToUnlink(null)
  }

  /**
   * Delete a picture from the gallery after confirmation
   * This will remove the picture from the list AND delete it from the server
   */
  const handleDeleteConfirm = async () => {
    if (!pictureToDelete) return

    const { picture, index } = pictureToDelete
    setDeletingImageId(picture.id)

    try {
      const originalPicture = pictures[index]
      let resourceId = null

      if (typeof originalPicture === 'object' && originalPicture.resourceId) {
        resourceId = originalPicture.resourceId
      }

      if (resourceId) {
        try {
          await ImageService.deleteImage(resourceId)
        } catch (deleteError) {
          console.warn('Failed to delete image from server:', deleteError)
        }
      }

      const updatedPictures = pictures.filter((_, i) => i !== index)

      if (onPicturesChange) {
        await onPicturesChange(updatedPictures)
      }

      bar.success('Picture deleted from gallery and server successfully')
      setDeleteConfirmOpen(false)
      setPictureToDelete(null)
    } catch (error) {
      console.error('Failed to delete picture:', error)
      bar.error(error.message || 'Failed to delete picture from gallery')
    } finally {
      setDeletingImageId(null)
    }
  }

  /**
   * Unlink a picture from the gallery after confirmation
   * This will remove the picture from the list but NOT delete it from the server
   */
  const handleUnlinkConfirm = async () => {
    if (!pictureToUnlink) return

    const { index } = pictureToUnlink

    try {
      const updatedPictures = pictures.filter((_, i) => i !== index)

      if (onPicturesChange) {
        await onPicturesChange(updatedPictures)
      }

      bar.success('Picture unlinked from gallery successfully')
      setUnlinkConfirmOpen(false)
      setPictureToUnlink(null)
    } catch (error) {
      console.error('Failed to unlink picture:', error)
      bar.error(error.message || 'Failed to unlink picture from gallery')
    }
  }

  /**
   * Open attach existing pictures dialog
   */
  const handleOpenAttachDialog = async () => {
    setOpenAttachDialog(true)
    setSelectedExistingImages([])
    setLoadingExistingImages(true)

    try {
      const response = await ImageService.getImages({ ownerId: user.resourceId })
      setExistingImages(response || [])
    } catch (error) {
      console.error('Failed to load existing images:', error)
      bar.error(error.message || 'Failed to load existing images')
    } finally {
      setLoadingExistingImages(false)
    }
  }

  /**
   * Close attach existing pictures dialog
   */
  const handleCloseAttachDialog = () => {
    setOpenAttachDialog(false)
    setSelectedExistingImages([])
    setExistingImages([])
  }

  /**
   * Toggle selection of an existing image
   */
  const handleToggleExistingImage = (image) => {
    setSelectedExistingImages((prev) => {
      const isSelected = prev.some((img) => img.resourceId === image.resourceId)
      if (isSelected) {
        return prev.filter((img) => img.resourceId !== image.resourceId)
      } else {
        return [...prev, image]
      }
    })
  }

  /**
   * Attach selected existing images to gallery
   */
  const handleAttachExistingImages = async () => {
    if (selectedExistingImages.length === 0) {
      bar.warn('Please select at least one image to attach')
      return
    }

    setAttachingImages(true)

    try {
      const newPictures = selectedExistingImages.map((image) => ({
        resourceId: image.resourceId,
        url: image.r2Url || image.url,
        r2Url: image.r2Url,
      }))

      // Filter out duplicates based on resourceId
      const existingResourceIds = new Set(pictures.filter((pic) => typeof pic === 'object' && pic.resourceId).map((pic) => pic.resourceId))

      const uniqueNewPictures = newPictures.filter((pic) => !existingResourceIds.has(pic.resourceId))

      if (uniqueNewPictures.length === 0) {
        bar.warn('All selected images are already in the gallery')
        setAttachingImages(false)
        return
      }

      const updatedPictures = [...pictures, ...uniqueNewPictures]

      if (onPicturesChange) {
        await onPicturesChange(updatedPictures)
      }

      bar.success(`${uniqueNewPictures.length} picture(s) attached to gallery successfully`)
      handleCloseAttachDialog()
    } catch (error) {
      console.error('Failed to attach pictures:', error)
      bar.error(error.message || 'Failed to attach pictures to gallery')
    } finally {
      setAttachingImages(false)
    }
  }

  /**
   * Handle image click to open fullscreen viewer
   */
  const handleImageClick = (index) => {
    setViewerInitialIndex(index)
    setViewerOpen(true)
  }

  /**
   * Handle close fullscreen viewer
   */
  const handleCloseViewer = () => {
    setViewerOpen(false)
  }

  return (
    <Box>
      {normalizedPictures.length > 0 ? (
        <ImageList variant="masonry" cols={3} gap={8}>
          {normalizedPictures.map((picture, index) => (
            <ImageListItem key={picture.id}>
              <img
                src={picture.url}
                alt={`Gallery ${index + 1}`}
                loading="lazy"
                onClick={() => handleImageClick(index)}
                style={{
                  borderRadius: 8,
                  display: 'block',
                  width: '100%',
                  cursor: 'pointer',
                }}
              />
              {editable && (
                <ImageListItemBar
                  sx={{
                    background: 'transparent',
                    '&:hover .action-button': {
                      opacity: 1,
                      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
                    },
                  }}
                  position="top"
                  actionIcon={
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        className="action-button"
                        sx={{
                          color: 'white',
                          bgcolor: 'rgba(0, 0, 0, 0.3)',
                          opacity: 0.4,
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            bgcolor: 'warning.main',
                            opacity: 1,
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
                            transform: 'scale(1.05)',
                          },
                        }}
                        onClick={() => handleUnlinkClick(picture, index)}
                        title="Unlink (remove from gallery but keep on server)"
                      >
                        <LinkOffIcon />
                      </IconButton>
                      <IconButton
                        className="action-button"
                        sx={{
                          color: 'white',
                          bgcolor: 'rgba(0, 0, 0, 0.3)',
                          opacity: 0.4,
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            bgcolor: 'error.main',
                            opacity: 1,
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
                            transform: 'scale(1.05)',
                          },
                        }}
                        onClick={() => handleDeleteClick(picture, index)}
                        disabled={deletingImageId === picture.id}
                        title="Delete (remove from gallery and delete from server)"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  }
                  actionPosition="right"
                />
              )}
            </ImageListItem>
          ))}
        </ImageList>
      ) : (
        <Card variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: 'action.hover' }}>
          <Typography variant="body2" color="text.secondary">
            No pictures yet. {editable && 'Click "Upload Pictures" to upload new photos or "Attach Pictures" to link existing ones.'}
          </Typography>
        </Card>
      )}

      {editable && (
        <CardActions sx={{ justifyContent: 'center', mt: 2, gap: 1 }}>
          <Button variant="outlined" startIcon={<CloudUploadIcon />} onClick={handleOpenUploadDialog}>
            Upload Pictures
          </Button>
          <Button variant="outlined" startIcon={<AttachFileIcon />} onClick={handleOpenAttachDialog}>
            Attach Pictures
          </Button>
        </CardActions>
      )}

      {/* Upload Dialog */}
      <Dialog open={openUploadDialog} onClose={handleCloseUploadDialog} maxWidth="md" fullWidth>
        <DialogTitle>Upload Pictures to Gallery</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {uploadedImages.length > 0 ? (
              // Show uploaded images preview
              <Box>
                <Typography variant="body2" color="success.main" sx={{ mb: 2 }}>
                  ✓ {uploadedImages.length} image(s) uploaded successfully
                </Typography>
                <Grid container spacing={2}>
                  {uploadedImages.map((imageData, index) => (
                    <Grid key={index} size={{ xs: 6, sm: 4 }}>
                      <img
                        src={imageData.r2Url}
                        alt={`Uploaded ${index + 1}`}
                        style={{
                          width: '100%',
                          borderRadius: 8,
                          display: 'block',
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                  Click "Confirm" below to add these pictures to your gallery, or "Cancel" to remove them.
                </Typography>
              </Box>
            ) : selectedFiles.length > 0 ? (
              // Show selected files preview
              <Box>
                <Typography variant="body2" color="primary" sx={{ mb: 2 }}>
                  {selectedFiles.length} file(s) selected. Click "Upload" to upload them.
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {selectedFiles.map((fileItem) => (
                    <Grid key={fileItem.id} size={{ xs: 6, sm: 4 }}>
                      <Box sx={{ position: 'relative' }}>
                        <img
                          src={fileItem.previewUrl}
                          alt={fileItem.file.name}
                          style={{
                            width: '100%',
                            borderRadius: 8,
                            display: 'block',
                          }}
                        />
                        <IconButton
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            bgcolor: 'background.paper',
                            '&:hover': { bgcolor: 'error.main', color: 'white' },
                          }}
                          onClick={() => handleRemoveSelectedFile(fileItem.id)}
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Typography variant="caption" noWrap>
                        {fileItem.file.name}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ) : (
              // Show drag and drop area
              <Paper
                variant="outlined"
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                sx={{
                  p: 4,
                  textAlign: 'center',
                  cursor: 'pointer',
                  bgcolor: isDragging ? 'action.hover' : 'background.paper',
                  borderStyle: 'dashed',
                  borderWidth: 2,
                  borderColor: isDragging ? 'primary.main' : 'divider',
                  transition: 'all 0.2s',
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" gutterBottom>
                  Drag and drop images here, or click to select
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Supported formats: JPEG, PNG, GIF, WEBP (max 10MB each)
                </Typography>
                <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 1 }}>
                  You can select multiple files at once
                </Typography>
              </Paper>
            )}

            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" multiple style={{ display: 'none' }} onChange={handleFileInputChange} />
          </Box>
        </DialogContent>
        <DialogActions>
          {uploadedImages.length > 0 ? (
            <>
              <Button onClick={handleCancelAllUploads} disabled={isConfirming}>
                Cancel
              </Button>
              <Button onClick={handleConfirmUpload} variant="contained" disabled={isConfirming}>
                {isConfirming ? 'Adding...' : 'Confirm'}
              </Button>
            </>
          ) : selectedFiles.length > 0 ? (
            <>
              <Button onClick={handleCloseUploadDialog} disabled={isUploading}>
                Close
              </Button>
              <Button onClick={handleUploadFiles} variant="contained" startIcon={<CloudUploadIcon />} disabled={isUploading}>
                {isUploading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Uploading...
                  </>
                ) : (
                  `Upload ${selectedFiles.length} file(s)`
                )}
              </Button>
            </>
          ) : (
            <Button onClick={handleCloseUploadDialog}>Close</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Picture</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to delete this picture?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Delete</strong> will remove it from the pictures list and delete it from the server. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deletingImageId !== null}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error" disabled={deletingImageId !== null}>
            {deletingImageId !== null ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unlink Confirmation Dialog */}
      <Dialog open={unlinkConfirmOpen} onClose={handleUnlinkCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Unlink Picture</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to unlink this picture?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Unlink</strong> will remove it from the pictures list but will not delete it from the server. You can add it back later if needed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUnlinkCancel}>Cancel</Button>
          <Button onClick={handleUnlinkConfirm} variant="contained" color="warning">
            Unlink
          </Button>
        </DialogActions>
      </Dialog>

      {/* Attach Existing Pictures Dialog */}
      <Dialog open={openAttachDialog} onClose={handleCloseAttachDialog} maxWidth="md" fullWidth>
        <DialogTitle>Attach Existing Pictures</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {loadingExistingImages ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : existingImages.length > 0 ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select pictures to attach to this gallery. {selectedExistingImages.length > 0 && `(${selectedExistingImages.length} selected)`}
                </Typography>
                <ImageList variant="masonry" cols={3} gap={8}>
                  {existingImages.map((image) => {
                    const isSelected = selectedExistingImages.some((img) => img.resourceId === image.resourceId)
                    const isAlreadyInGallery = pictures.some((pic) => typeof pic === 'object' && pic.resourceId === image.resourceId)

                    return (
                      <ImageListItem
                        key={image.resourceId}
                        sx={{
                          cursor: isAlreadyInGallery ? 'not-allowed' : 'pointer',
                          opacity: isAlreadyInGallery ? 0.5 : 1,
                          position: 'relative',
                          border: isSelected ? 3 : 0,
                          borderColor: 'primary.main',
                          borderRadius: 1,
                        }}
                        onClick={() => !isAlreadyInGallery && handleToggleExistingImage(image)}
                      >
                        <img
                          src={image.r2Url || image.url}
                          alt={`Gallery item ${image.resourceId}`}
                          loading="lazy"
                          style={{
                            borderRadius: 8,
                            display: 'block',
                            width: '100%',
                          }}
                        />
                        <ImageListItemBar
                          sx={{
                            background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)',
                          }}
                          position="bottom"
                          actionIcon={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
                              {isAlreadyInGallery ? (
                                <Typography variant="caption" sx={{ color: 'white', fontStyle: 'italic' }}>
                                  Already in gallery
                                </Typography>
                              ) : (
                                <Checkbox checked={isSelected} icon={<CheckBoxOutlineBlankIcon />} checkedIcon={<CheckBoxIcon />} sx={{ color: 'white' }} />
                              )}
                            </Box>
                          }
                          actionPosition="right"
                        />
                      </ImageListItem>
                    )
                  })}
                </ImageList>
              </>
            ) : (
              <Card variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: 'action.hover' }}>
                <Typography variant="body2" color="text.secondary">
                  No existing pictures found. Upload new pictures first.
                </Typography>
              </Card>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAttachDialog} disabled={attachingImages}>
            Cancel
          </Button>
          <Button onClick={handleAttachExistingImages} variant="contained" disabled={selectedExistingImages.length === 0 || attachingImages}>
            {attachingImages ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Attaching...
              </>
            ) : (
              `Attach ${selectedExistingImages.length > 0 ? selectedExistingImages.length : ''} Picture${selectedExistingImages.length !== 1 ? 's' : ''}`
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fullscreen Media Viewer */}
      <FullscreenMediaViewer
        open={viewerOpen}
        onClose={handleCloseViewer}
        media={normalizedPictures.map((pic) => ({
          type: 'image',
          url: pic.url,
        }))}
        initialIndex={viewerInitialIndex}
      />
    </Box>
  )
}

export default PictureManagement
